import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ColumnDef } from '@/components/UI/dataTableConfig';

import UserLayout from '@/components/Layout/UserLayout';
import { ResponsiveTableCard, Button, DataTable } from '@/components/UI';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner';
import { trpc } from '@/api/trpc';
import { useAppQuery } from '@/hooks/useAppQuery';
import { DEFAULT_REQUISITIES_STALE_TIME } from '@/config/constants';
import type { OptionBuyingStrategy, ResourceAccessListItem } from '@einhaus/project314-types';
import ResourceAccessBadge from '@/components/resourceAccess/ResourceAccessBadge';
import ShareResourceModal from '@/components/resourceAccess/ShareResourceModal';
import { formatOpeningAction } from '@/utils/tradeSemantics';

const OptionBuyingStrategiesPage: React.FC = () => {
    const navigate = useNavigate();
    const [error, setError] = useState<string | null>(null);
    const [sharingStrategy, setSharingStrategy] = useState<{ id: number; label: string } | null>(null);

    const strategiesQuery = useAppQuery(
        {
            ...trpc.strategy.fetchOptionBuyingStrategies.queryOptions({}),
            staleTime: DEFAULT_REQUISITIES_STALE_TIME
        },
        {
            fallbackErrorMessage: 'Failed to load option buying strategies',
            showErrorToast: true,
            setError: (msg) => setError(msg)
        }
    );

    const accessQuery = useAppQuery(
        {
            ...trpc.resourceAccess.listAccessibleResources.queryOptions({
                resourceType: 'optionBuyingStrategy'
            }),
            staleTime: DEFAULT_REQUISITIES_STALE_TIME
        },
        {
            fallbackErrorMessage: 'Failed to load option buying strategy access state',
            showErrorToast: true
        }
    );

    const strategies = useMemo(() => strategiesQuery.data?.strategies ?? [], [strategiesQuery.data]);

    const accessByStrategyId = useMemo(() => {
        const map: Record<number, ResourceAccessListItem> = {};

        for (const item of accessQuery.data ?? []) {
            map[item.resource.resourcePk] = item;
        }

        return map;
    }, [accessQuery.data]);

    const columns: ColumnDef<OptionBuyingStrategy>[] = useMemo(
        () => [
            { accessorKey: 'id', header: 'ID', minSize: 60, size: 80, maxSize: 100 },
            {
                accessorKey: 'name',
                header: 'Name',
                minSize: 220,
                size: 360,
                maxSize: 520,
                cell: ({ row }) => (
                    <button
                        className="text-left text-blue-400 hover:text-blue-300 underline cursor-pointer"
                        type="button"
                        onClick={() => navigate(`/user/optionBuyingStrategy/${row.original.id}`)}
                    >
                        {row.original.name ?? '(Unnamed)'}
                    </button>
                )
            },
            { accessorKey: 'buyingMethod', header: 'Method', minSize: 150, size: 200, maxSize: 260 },
            {
                accessorKey: 'openTransactionType',
                header: 'Opening action',
                minSize: 120,
                size: 150,
                maxSize: 190,
                cell: ({ row }) => formatOpeningAction(row.original.openTransactionType)
            },
            {
                id: 'dte',
                header: 'DTE (min/target/max)',
                minSize: 170,
                size: 220,
                maxSize: 260,
                cell: ({ row }) => {
                    const s = row.original;
                    const min = s.minimumDaysToExpiry ?? '-';
                    const target = s.targetDaysToExpiry ?? '-';
                    const max = s.maximumDaysToExpiry ?? '-';
                    return (
                        <span className="text-zinc-300">
                            {min}/{target}/{max}
                        </span>
                    );
                }
            },
            {
                accessorKey: 'minimumPrice',
                header: 'Min Price',
                minSize: 90,
                size: 110,
                maxSize: 150,
                cell: ({ row }) => <span className="text-zinc-300">{row.original.minimumPrice ?? '-'}</span>
            },
            {
                accessorKey: 'maximumSpreadPercentage',
                header: 'Max Spread %',
                minSize: 120,
                size: 140,
                maxSize: 180,
                cell: ({ row }) => <span className="text-zinc-300">{row.original.maximumSpreadPercentage ?? '-'}</span>
            },
            {
                accessorKey: 'autoBacktest',
                header: 'Auto',
                minSize: 80,
                size: 90,
                maxSize: 120,
                cell: ({ row }) =>
                    row.original.autoBacktest ? <span className="text-green-400">Yes</span> : <span className="text-zinc-400">No</span>
            },
            {
                accessorKey: 'active',
                header: 'Status',
                minSize: 90,
                size: 110,
                maxSize: 150,
                cell: ({ row }) => (
                    <span
                        className={`px-2 py-1 rounded text-xs font-medium ${
                            row.original.active
                                ? 'bg-green-900/50 text-green-300 border border-green-700'
                                : 'bg-zinc-700 text-zinc-400 border border-zinc-600'
                        }`}
                    >
                        {row.original.active ? 'Active' : 'Inactive'}
                    </span>
                )
            },
            {
                id: 'access',
                header: 'Access',
                minSize: 110,
                size: 130,
                maxSize: 170,
                cell: ({ row }) => {
                    const access = accessByStrategyId[row.original.id];

                    if (!access) {
                        return <span className="text-xs text-zinc-500">Unknown</span>;
                    }

                    return <ResourceAccessBadge access={access} />;
                }
            },
            {
                id: 'experiment',
                header: 'Experiment',
                minSize: 100,
                size: 120,
                maxSize: 150,
                cell: ({ row }) =>
                    row.original.isExperimentOnly ? <span className="text-amber-300">Yes</span> : <span className="text-zinc-500">No</span>,
                enableHiding: true
            },
            {
                id: 'actions',
                header: () => <span className="hidden sm:inline">Actions</span>,
                minSize: 160,
                size: 220,
                maxSize: 300,
                cell: ({ row }) => {
                    const strategy = row.original;
                    const access = accessByStrategyId[strategy.id];
                    const canEdit = access?.canEdit ?? true;
                    const canShare = access?.ownedByUser ?? false;
                    return (
                        <div className="flex gap-1 sm:gap-2" role="presentation" onClick={(e) => e.stopPropagation()}>
                            <button
                                className="px-1 py-1 sm:px-2 text-xs text-zinc-300 hover:text-white bg-zinc-800 hover:bg-zinc-700 rounded transition-colors"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    void navigate(`/user/optionBuyingStrategy/${strategy.id}`);
                                }}
                            >
                                <span className="hidden sm:inline">{canEdit ? 'Edit' : 'Open'}</span>
                                <span className="sm:hidden">✏️</span>
                            </button>
                            <button
                                className="px-1 py-1 sm:px-2 text-xs text-zinc-300 hover:text-white bg-zinc-800 hover:bg-zinc-700 rounded transition-colors hidden sm:inline-block"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    void navigate(`/user/optionBuyingStrategy/new?copyFrom=${strategy.id}`);
                                }}
                            >
                                Copy
                            </button>
                            {canShare && (
                                <button
                                    className="px-1 py-1 sm:px-2 text-xs text-zinc-300 hover:text-white bg-zinc-800 hover:bg-zinc-700 rounded transition-colors hidden sm:inline-block"
                                    onClick={(e) => {
                                        e.stopPropagation();

                                        setSharingStrategy({
                                            id: strategy.id,
                                            label: strategy.name ?? `Option Buying Strategy #${strategy.id}`
                                        });
                                    }}
                                >
                                    Share
                                </button>
                            )}
                        </div>
                    );
                }
            }
        ],
        [accessByStrategyId, navigate]
    );

    return (
        <UserLayout title="Option Buying Strategies - Project 314">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-2 sm:mb-3 md:mb-4 gap-2">
                <h3 className="mb-0 text-white text-lg sm:text-xl font-semibold">Option Buying Strategies</h3>
                <Button className="text-sm sm:text-base" to="/user/optionBuyingStrategy/new" variant="primary">
                    Create Strategy
                </Button>
            </div>

            {error && (
                <div className="mb-4">
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
                        <strong className="font-bold">Error: </strong>
                        <span className="block sm:inline">{error}</span>
                    </div>
                </div>
            )}

            <ResponsiveTableCard noCard>
                {strategiesQuery.isLoading ? (
                    <div className="flex justify-center py-8">
                        <DataLoadingSpinner />
                    </div>
                ) : (
                    <DataTable
                        allowOverflow
                        search
                        sort
                        columns={columns}
                        data={strategies}
                        pagination={{ enabled: true, pageSize: 15 }}
                        onRowClick={(row) => navigate(`/user/optionBuyingStrategy/${row.id}`)}
                    />
                )}
            </ResponsiveTableCard>

            {sharingStrategy && (
                <ShareResourceModal
                    isOpen={!!sharingStrategy}
                    resourceLabel={sharingStrategy.label}
                    resourcePk={sharingStrategy.id}
                    resourceType="optionBuyingStrategy"
                    onClose={() => setSharingStrategy(null)}
                />
            )}
        </UserLayout>
    );
};

export default OptionBuyingStrategiesPage;
